import { useEffect, useState } from 'react';
import { Page, Card, Text, TextField, Select, BlockStack, Checkbox, Banner } from '@shopify/polaris';
import { Link, useLocation } from 'react-router-dom';
import { settingsRequest, settingsError } from '../api/adminSettings';
import { SettingsFeedback, SettingsLoading } from '../components/SettingsFields';
import { API_DOMAIN } from '../utils/constants';

const policies = [['rp', 'Refund policy'], ['sp', 'Shipping policy'], ['pp', 'Privacy policy'], ['tos', 'Terms of service'], ['co', 'Contact'], ['promise', 'Checkout promise']];
const codes = [['header_code', 'Header HTML'], ['header_js_code', 'Header JavaScript'], ['bottom_js_code', 'Footer JavaScript']];
export default function Settings() {
  const location = useLocation();
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const load = async () => { setError(''); try {
    const [settings, response] = await Promise.all([settingsRequest('g-checkout-settings'), fetch(API_DOMAIN + '/api/states.json')]);
    if (!response.ok) throw new Error('Unable to load countries.');
    const body = await response.json();
    setCountries((body.data?.countries || []).map(c => ({ label: c.country_name + ' (' + c.country_code + ')', value: c.country_code })));
    setData({ ...settings, google_address_enabled: settings.google_address_enabled ?? true });
  } catch (e) { setError(settingsError(e)); } };
  useEffect(() => { load(); }, []);
  const change = key => value => { setData(prev => ({ ...prev, [key]: value })); setSuccess(''); };
  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const { google_address_configured, ...input } = data;
      setData(await settingsRequest('u-checkout-settings', input)); setSuccess('Settings saved.');
    }
    catch (e) { setError(settingsError(e)); }
    finally { setSaving(false); }
  };
  return <Page primaryAction={{ content: 'Save settings', onAction: save, loading: saving, disabled: !data }}>
    <div className="settings-stack"><SettingsFeedback error={error} success={success} retry={!data ? load : undefined} />
      {!data ? (!error && <SettingsLoading />) : <>
        <Card><BlockStack gap="400"><Text as="h2" variant="headingMd">Branding &amp; address</Text>
          <div className="settings-branding"><BlockStack gap="200"><Text as="p">Store logo</Text><div className="settings-logo-preview">{/^https?:\/\//i.test(data.logo_url) ? <img className="settings-logo" src={data.logo_url} alt="Store logo" /> : <Text as="p" tone="subdued">No logo</Text>}</div></BlockStack><TextField label="Logo URL" value={data.logo_url} onChange={change('logo_url')} autoComplete="off" disabled={saving} helpText="Enter a publicly accessible URL to your logo (SVG, PNG or JPG)." />
            <Select label="Default country" options={countries} value={data.default_country} onChange={change('default_country')} disabled={saving} helpText="Selected by default for the checkout address." /></div>
          <Checkbox label="Enable Google address autocomplete" checked={Boolean(data.google_address_enabled)} onChange={change('google_address_enabled')} disabled={saving} helpText="Uses the Google Maps key configured for this store. Applies to online checkout only." />
          {data.google_address_enabled && !data.google_address_configured && <Banner tone="warning">
            Google address suggestions are unavailable because this store has no Google Maps API key. Add one under <Link to={'/integrations/google-maps' + location.search}>Integrations → Google Maps</Link>.
          </Banner>}
        </BlockStack></Card>
        <Card><BlockStack gap="400"><Text as="h2" variant="headingMd">Policies &amp; contact</Text><div className="settings-grid">
          {policies.map(([key, label]) => <TextField key={key} label={label} value={data[key]} onChange={change(key)} multiline={3} maxHeight="240px" autoComplete="off" disabled={saving} />)}
        </div></BlockStack></Card>
        <Card><details className="settings-code"><summary>Custom code</summary><BlockStack gap="400">
          {codes.map(([key, label]) => <TextField key={key} label={label} value={data[key]} onChange={change(key)} multiline={5} maxHeight="300px" autoComplete="off" monospaced disabled={saving} />)}
        </BlockStack></details></Card>
      </>}
    </div>
  </Page>;
}
